import imageCompression from 'browser-image-compression';

const MAX_SIZE_MB = 2; // 2MB threshold
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

/**
 * Compresses an image file if it's larger than 2MB
 * @param {File} file - The image file to compress
 * @returns {Promise<File>} - The compressed file (or original if already small enough)
 */
export const compressImageIfNeeded = async (file) => {
  console.log(`[Image Compression] Processing file: ${file.name}, type: ${file.type}, size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
  
  // Only compress image files
  const isImage = file.type.startsWith('image/');
  if (!isImage) {
    console.log(`[Image Compression] Not an image file (${file.type}), skipping compression`);
    return file; // Return non-image files as-is
  }

  // If file is already under 2MB, return as-is
  if (file.size <= MAX_SIZE_BYTES) {
    console.log(`[Image Compression] File is ${(file.size / 1024 / 1024).toFixed(2)}MB, no compression needed`);
    return file;
  }
  
  console.log(`[Image Compression] File is ${(file.size / 1024 / 1024).toFixed(2)}MB, starting compression...`);

  try {
    const options = {
      maxSizeMB: MAX_SIZE_MB, // Maximum size in MB (target: 2MB)
      maxWidthOrHeight: 1920, // Maximum width or height (maintains aspect ratio)
      useWebWorker: true, // Use web worker for better performance
      fileType: file.type, // Maintain original file type
      initialQuality: 0.8, // Start with 80% quality
      alwaysKeepResolution: false, // Allow resizing if needed
    };

    const compressedFile = await imageCompression(file, options);
    
    const originalSizeMB = (file.size / 1024 / 1024).toFixed(2);
    const compressedSizeMB = (compressedFile.size / 1024 / 1024).toFixed(2);
    const reductionPercent = ((1 - compressedFile.size / file.size) * 100).toFixed(1);
    
    console.log(`[Image Compression] ✅ Success! ${originalSizeMB}MB → ${compressedSizeMB}MB (${reductionPercent}% reduction)`);
    
    // If compression didn't reduce size enough, try more aggressive compression
    if (compressedFile.size > MAX_SIZE_BYTES) {
      console.log(`[Image Compression] File still too large (${compressedSizeMB}MB), applying more aggressive compression...`);
      const aggressiveOptions = {
        maxSizeMB: MAX_SIZE_MB,
        maxWidthOrHeight: 1600,
        useWebWorker: true,
        fileType: file.type,
        initialQuality: 0.6, // Lower quality for better compression
        alwaysKeepResolution: false,
      };
      
      const moreCompressed = await imageCompression(file, aggressiveOptions);
      const finalSizeMB = (moreCompressed.size / 1024 / 1024).toFixed(2);
      console.log(`[Image Compression] ✅ Aggressive compression: ${originalSizeMB}MB → ${finalSizeMB}MB`);
      return moreCompressed;
    }
    
    return compressedFile;
  } catch (error) {
    console.error('[Image Compression] ❌ Compression failed:', error);
    console.error('[Image Compression] Error details:', {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
    // If compression fails, return original file
    console.warn('[Image Compression] Using original file due to compression error');
    return file;
  }
};

